import React, { useState } from 'react';
import { observer } from 'mobx-react';
import BackgroundView from './BackgroundView';
import DifferentiatorsView from './DifferentiatorsView';
import ProblemsView from './ProblemsView';
import CompetitorsView from './CompetitorsView';
import ProspectProfileView from './ProspectProfileView';
import EditWrapper from './EditWrapper';
import ItemSwitcher from '../components/ItemSwitcher';
import useQueryParam from '../components/useQueryParam';

// V1 edit components (reused as-is for edit mode)
import CompanyBackground from '../../components/CompanyOverview/CompanyBackground';
import Differentiators from '../../components/CompanyOverview/Differentiators';
import AddressableProblems from '../../components/CompanyOverview/AddressableProblems';
import TopCompetitors from '../../components/CompanyOverview/TopCompetitors';
import ProspectProfile from '../../components/CompanyOverview/ProspectProfile';
import useUnsavedGuard from '../components/useUnsavedGuard';

import {
  PageWrapper, PageHeader, PageTitle, EditButton, EmptyState, InlineLink, PageSkeleton,
} from './CompanyPage.styles';

const SECTIONS = [
  { key: 'background', label: 'Background', noun: 'background details' },
  { key: 'differentiators', label: 'Differentiators', noun: 'differentiators' },
  { key: 'competitors', label: 'Competitors', noun: 'competitors' },
  { key: 'problems', label: 'Addressable Problems', noun: 'addressable problems' },
  { key: 'prospects', label: 'Prospect Profile', noun: 'prospect profiles' },
];
const DEFAULT_SECTION = 'background';

const filled = (arr) => (Array.isArray(arr) ? arr.filter((x) => x && (typeof x !== 'string' || x.trim())) : []);
const plural = (n, noun) => `${n} ${noun}${n === 1 ? '' : 's'}`;

// What each section holds, for the line under its name in the rail: a
// completeness check at a glance ("No competitors yet").
export const sectionCaption = (key, company) => {
  switch (key) {
    case 'background': {
      const b = company?.background || {};
      const parts = [];
      if (b.yearFounded > 0) parts.push(`Founded ${b.yearFounded}`);
      if (b.numberOfEmployees > 0) parts.push(`${Number(b.numberOfEmployees).toLocaleString()} employees`);
      const texts = [b.history && 'history', b.mission && 'mission'].filter(Boolean);
      if (texts.length) parts.push(texts.join(' and '));
      return parts.length ? parts.join(', ') : 'Nothing added yet';
    }
    case 'differentiators': { const n = filled(company?.differentiators).length; return n ? plural(n, 'differentiator') : 'No differentiators yet'; }
    case 'competitors': { const n = filled(company?.topCompetitors).length; return n ? plural(n, 'competitor') : 'No competitors yet'; }
    case 'problems': { const n = filled(company?.addressableProblems).length; return n ? plural(n, 'problem') : 'No problems yet'; }
    case 'prospects': { const n = filled(company?.prospectProfile).length; return n ? plural(n, 'profile') : 'No profiles yet'; }
    default: return null;
  }
};

export const sectionIsEmpty = (key, company) => {
  switch (key) {
    case 'background': {
      const b = company?.background || {};
      return !(b.ceo || b.yearFounded > 0 || b.numberOfEmployees > 0 || b.numberOfOffices > 0 || b.history || b.mission || b.uploadLink || b.uploadKey);
    }
    case 'differentiators': return filled(company?.differentiators).length === 0;
    case 'competitors': return filled(company?.topCompetitors).length === 0;
    case 'problems': return filled(company?.addressableProblems).length === 0;
    case 'prospects': return filled(company?.prospectProfile).length === 0;
    default: return false;
  }
};

const CompanyPage = observer(({ company, mode }) => {
  // The selected section lives in the URL (?section=competitors): a refresh
  // keeps it, the back button steps through sections, the address is shareable.
  const [sectionParam, setSectionParam] = useQueryParam('section', DEFAULT_SECTION);
  const [editing, setEditing] = useState(false);
  const { onDirtyChange, confirmLeave } = useUnsavedGuard();
  const isReadonly = mode === 'readonly';
  const loaded = !!company?.id;
  const activeTab = SECTIONS.some((s) => s.key === sectionParam) ? sectionParam : DEFAULT_SECTION;

  const renderReadonlyView = (key) => {
    const section = SECTIONS.find((s) => s.key === key);
    if (section && sectionIsEmpty(key, company)) {
      return (
        <EmptyState>
          {`No ${section.noun} yet.`}
          {!isReadonly && (
            <>
              {' '}
              <InlineLink type="button" onClick={() => setEditing(true)}>Add them</InlineLink>
            </>
          )}
        </EmptyState>
      );
    }
    switch (key) {
      case 'background':
        return <BackgroundView background={company.background} />;
      case 'differentiators':
        return <DifferentiatorsView differentiators={company.differentiators} />;
      case 'problems':
        return <ProblemsView problems={company.addressableProblems} />;
      case 'competitors':
        return <CompetitorsView competitors={company.topCompetitors} />;
      case 'prospects':
        return <ProspectProfileView profiles={company.prospectProfile} />;
      default:
        return null;
    }
  };

  const renderEditView = (key) => {
    const onBack = () => { if (confirmLeave()) setEditing(false); };

    switch (key) {
      case 'background':
        return (
          <EditWrapper onBack={onBack}>
            <CompanyBackground companyBackground={company.background} onDirtyChange={onDirtyChange} />
          </EditWrapper>
        );
      case 'differentiators':
        return (
          <EditWrapper onBack={onBack}>
            <Differentiators company={company} onDirtyChange={onDirtyChange} />
          </EditWrapper>
        );
      case 'problems':
        return (
          <EditWrapper onBack={onBack}>
            <AddressableProblems company={company} onDirtyChange={onDirtyChange} />
          </EditWrapper>
        );
      case 'competitors':
        return (
          <EditWrapper onBack={onBack}>
            <TopCompetitors company={company} onDirtyChange={onDirtyChange} />
          </EditWrapper>
        );
      case 'prospects':
        return (
          <EditWrapper onBack={onBack}>
            <ProspectProfile company={company} onDirtyChange={onDirtyChange} />
          </EditWrapper>
        );
      default:
        return null;
    }
  };

  const items = SECTIONS.map((s) => ({ key: s.key, label: s.label, caption: sectionCaption(s.key, company) }));

  return (
    <PageWrapper data-tour="company-page">
      <PageHeader>
        <PageTitle>Company Overview</PageTitle>
        {loaded && !isReadonly && !editing && (
          <EditButton onClick={() => setEditing(true)}>
            {'✎'} Edit
          </EditButton>
        )}
      </PageHeader>

      {/* The same switcher as Offerings, so the business-data pages read alike:
          a section list beside the content on desktop, a wrapping bar when the
          column is narrow, an accordion on a phone. The section views bring
          their own cards, so the detail is unframed, and fixed sections get no
          "1 of 5" counter. Switching sections leaves edit mode. */}
      {!loaded ? (
        <PageSkeleton>
          <div style={{ flex: '0 0 280px', height: 300 }} />
          <div style={{ flex: 1, height: 360 }} />
        </PageSkeleton>
      ) : (
        <ItemSwitcher
          items={items}
          activeKey={activeTab}
          onChange={(key) => { if (editing && !confirmLeave()) return; setSectionParam(key); setEditing(false); }}
          canChange={() => !editing || confirmLeave()}
          renderDetail={(item) => (editing ? renderEditView(item.key) : renderReadonlyView(item.key))}
          ariaLabel="Company sections"
          countLabel="sections"
          frame="plain"
          position={false}
        />
      )}
    </PageWrapper>
  );
});

export default CompanyPage;
